import { Component, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { switchMap, tap } from 'rxjs/operators';
import { ContentCard } from 'src/app/models/content-card';
import { DataService } from 'src/app/services/data.service';
import { SiteService } from 'src/app/services/site.service';
import { TranslationService } from 'src/app/services/translation.service';
import { environment } from 'src/environments/environment';
import { TranslatablePageBaseComponent } from '../translatable-page-base.component';

@Component({
  selector: 'app-learn-more',
  templateUrl: './learn-more.component.html',
  styleUrls: ['./learn-more.component.scss'],
})
export class LearnMoreComponent extends TranslatablePageBaseComponent {
	public topicCards: Array<ContentCard> = [];
	public topicCardsToShow: Array<ContentCard> = [];
	public topicCardGroupsVisible: number = 1;
	public topicCardsPerGroup: number = 4;
	public env = environment;
	public heroImageUrl: string = '';
	public heroHeadingText: string = '';
	public outroText: string = '';
	public cta1Text: string = '';	public contactEapHeading: string = '';
	public currentLanguage: string = 'en';

	// Prototype: two videos on the Learn More page, switchable via a pill toggle.
	// 'eap' = What's an EAP (the existing Learn More video, default),
	// 'moments' = Moments of Realization (moved off the Home page).
	public videos: Array<{ key: string; label: string; url: string; poster: string }> = [
		{ key: 'eap', label: 'EAP', url: environment.learnMoreVideoURL, poster: '/assets/images/learn_more_video_poster.png' },
		{ key: 'moments', label: 'Real Stories', url: environment.homeVideoURL, poster: '/assets/images/home-video-poster.png' },
	];
	public selectedVideoKey: string = 'eap';

	get selectedVideo(): { key: string; label: string; url: string; poster: string } {
		return this.videos.find(v => v.key === this.selectedVideoKey) ?? this.videos[0];
	}

	selectVideo(key: string): void {
		this.selectedVideoKey = key;
	}

	getVttUrl(videoUrl: string, language: string): string {
		if (!videoUrl) return '';
		const baseUrl = videoUrl.split('?')[0];
		return baseUrl.match(/\.(mp4|webm|mov|avi|mkv|flv|wmv)$/i)
			? baseUrl.replace(/\.(mp4|webm|mov|avi|mkv|flv|wmv)$/i, `-${language}.vtt`)
			: `${baseUrl}-${language}.vtt`;
	}

	// FAQ content
	public faq1Question: string = '';
	public faq1Answer1: string = '';
	public faq1Answer2: string = '';
	public faq1Answer3: string = '';
	public faq2Question: string = '';
	public faq2Answer: string = '';
	public faq3Question: string = '';
	public faq3Answer: string = '';
	public faq4Question: string = '';
	public faq4Answer: string = '';
	public faq4SubAnswer1: string = '';
	public faq4SubAnswer2: string = '';
	public faq4SubAnswer3: string = '';
	public faq5Question: string = '';
	public faq5Answer: string = '';

	get eapData(): Record<string, string> {
		return { ...this.content[7], ...this.content[3], ...this.content[8] };
	}

	get heroBackgroundImage(): string {
		if (!this.heroImageUrl) return '';
		return `url("${this.heroImageUrl}")`;
	}

	sanitizePhone(phone: string): string {
		if (!phone) return '';
		return phone.replace(/\D/g, '');
	}

	private updateHeroBackground(): void {
		const overrideImage = this.content[6]?.['Learn More Hero Image Override'];
		const imageUrl = (overrideImage && overrideImage.trim()) ? overrideImage : '/assets/images/pexels-tim-douglas-6205523.jpg';
		
		// Set the property directly for template binding
		this.heroImageUrl = imageUrl;
		
		console.log('Learn More hero background updated:', { imageUrl: this.heroImageUrl });
	}

	private updateTextsWithHtml(): void {
		const currentLang = this.translationService.getCurrentLanguage();

		// Build CTA button 1 label from new CTA Button fields
		const defaultVisitEapText = 'Visit Your <span class="notranslate">EAP</span>';
		const defaultCallEapText = 'Call Your <span class="notranslate">EAP</span>';
		const btn1Type = this.eapData['CTA Button 1 Type'];
		const btn1LabelOverride = this.eapData['CTA Button 1 Label Override'];
		let cta1Template = '';
		if (btn1Type === 'Visit EAP') {
			cta1Template = (btn1LabelOverride?.trim()) ? btn1LabelOverride : defaultVisitEapText;
		} else if (btn1Type === 'Call EAP') {
			cta1Template = (btn1LabelOverride?.trim()) ? btn1LabelOverride : defaultCallEapText;
		}

		const heroText = 'You are so much <em>more</em> than your job.';
		const outroTextTemplate = 'Find <em>your</em> way back to you.';
		const contactHeadingTemplate = 'Contact your <span class="notranslate">EAP</span> for more info';

		if (currentLang === 'en') {
			this.heroHeadingText = heroText;
			this.outroText = outroTextTemplate;
			this.cta1Text = cta1Template;
			this.contactEapHeading = contactHeadingTemplate;
		} else {
			const items = [
				{ id: 'learnmore-hero-heading', text: heroText },
				{ id: 'learnmore-outro-text', text: outroTextTemplate },
				{ id: 'learnmore-contact-heading', text: contactHeadingTemplate }
			];

			if (cta1Template) {
				items.push({ id: 'learnmore-cta1-text', text: cta1Template });
			}

			this.translationService.translatePage(
				this.employer.employerID,
				'/learn-more',
				items
			).subscribe(translations => {
				this.heroHeadingText = translations['learnmore-hero-heading'] || heroText;
				this.outroText = translations['learnmore-outro-text'] || outroTextTemplate;
				this.cta1Text = translations['learnmore-cta1-text'] || cta1Template;
				this.contactEapHeading = translations['learnmore-contact-heading'] || contactHeadingTemplate;
			});
		}
	}

	private updateFaqs(): void {
		const currentLang = this.translationService.getCurrentLanguage();
		
		// Define all FAQ content
		const faqContent = {
			faq1Question: 'How is <span class="notranslate">One Morning</span> different from my <span class="notranslate">Employee Assistance Program (EAP)</span>?',
			faq1Answer1: '<span class="notranslate">One Morning</span> is here to complement your <span class="notranslate">Employee Assistance Program (EAP)</span> by making it easier for you to connect with the mental health resources you already have access to.',
			faq1Answer2: 'Think of us as your friendly guide, here to help you find your way to the support you need. Although we\'re not therapists, we can point you toward licensed counselors through your <span class="notranslate">EAP</span>. Remember, your <span class="notranslate">EAP</span> isn\'t just for crisis situations. Talk through everyday solutions for everyday problems—24/7.',
			faq1Answer3: 'Together with your organization, we believe in creating a healthier, more supportive workplace where everyone can thrive both in and out of work.',
			faq2Question: 'What does it cost?',
			faq2Answer: 'Your <span class="notranslate">EAP</span> services are confidential and, in most cases, completely free to you, covered by your employer as part of your benefits package. This means you can access several sessions with a licensed counselor and other mental health resources without worrying about the cost. Curious about how many sessions you\'re eligible for or what\'s included? Check out our <a [routerLink]="[\'/learn-more\']">Learn More page</a> for further details.',
			faq3Question: 'How do I access my <span class="notranslate">EAP</span>?',
			faq3Answer: 'Getting the help you need is a phone call, text, or click away. You can visit the website, call or text their toll-free phone number, or download the app from your app store. If you\'re not sure where to start, <span class="notranslate">One Morning</span> can help point you in the right direction; reach out to the contact info below.',
			faq4Question: 'I\'m in!  How can I be an advocate in my organization?',
			faq4Answer: 'Advocacy starts with small, meaningful actions. Here are a few ways you can help make a difference:',
			faq4SubAnswer1: '<b>Share Your Story:</b> Personal stories can be powerful in encouraging others to seek help. If you\'ve had a positive experience with <span class="notranslate">One Morning</span>, your <span class="notranslate">EAP</span>, or simply want to share your personal story, consider sharing it with your colleagues. Want to share it with our <span class="notranslate">One Morning</span> team? Contact us at <span class="notranslate"><a href="mailto:hello@onemorning.com">hello&#64;onemorning.com</a></span>.',
			faq4SubAnswer2: '<b>Be a Reminder:</b> If you hear someone talking about challenges at work or in life, remind them that resources are available. Sometimes, just knowing someone cares can make a huge difference.',
			faq4SubAnswer3: '<b>Open Up:</b> Don\'t be afraid to talk openly about mental health. The more we normalize these conversations, the more we can break down the barriers that keep people from seeking support.',
			faq5Question: 'What if my <span class="notranslate">EAP</span> isn\'t responsive or not working for me or my family?',
			faq5Answer: '<b>Help us, help you- Email us at <span class="notranslate"><a href="mailto:hello@onemorning.com">hello&#64;onemorning.com</a></span></b> Tell us your experience so we tell your organization, we can\'t change what we don\'t know. We care about you and your family.'
		};
		
		if (currentLang === 'en') {
			// Use original English text
			this.faq1Question = faqContent.faq1Question;
			this.faq1Answer1 = faqContent.faq1Answer1;
			this.faq1Answer2 = faqContent.faq1Answer2;
			this.faq1Answer3 = faqContent.faq1Answer3;
			this.faq2Question = faqContent.faq2Question;
			this.faq2Answer = faqContent.faq2Answer;
			this.faq3Question = faqContent.faq3Question;
			this.faq3Answer = faqContent.faq3Answer;
			this.faq4Question = faqContent.faq4Question;
			this.faq4Answer = faqContent.faq4Answer;
			this.faq4SubAnswer1 = faqContent.faq4SubAnswer1;
			this.faq4SubAnswer2 = faqContent.faq4SubAnswer2;
			this.faq4SubAnswer3 = faqContent.faq4SubAnswer3;
			this.faq5Question = faqContent.faq5Question;
			this.faq5Answer = faqContent.faq5Answer;
		} else {
			// Request translations
			const items = [
				{ id: 'learnmore-faq1Question', text: faqContent.faq1Question },
				{ id: 'learnmore-faq1Answer1', text: faqContent.faq1Answer1 },
				{ id: 'learnmore-faq1Answer2', text: faqContent.faq1Answer2 },
				{ id: 'learnmore-faq1Answer3', text: faqContent.faq1Answer3 },
				{ id: 'learnmore-faq2Question', text: faqContent.faq2Question },
				{ id: 'learnmore-faq2Answer', text: faqContent.faq2Answer },
				{ id: 'learnmore-faq3Question', text: faqContent.faq3Question },
				{ id: 'learnmore-faq3Answer', text: faqContent.faq3Answer },
				{ id: 'learnmore-faq4Question', text: faqContent.faq4Question },
				{ id: 'learnmore-faq4Answer', text: faqContent.faq4Answer },
				{ id: 'learnmore-faq4SubAnswer1', text: faqContent.faq4SubAnswer1 },
				{ id: 'learnmore-faq4SubAnswer2', text: faqContent.faq4SubAnswer2 },
				{ id: 'learnmore-faq4SubAnswer3', text: faqContent.faq4SubAnswer3 },
				{ id: 'learnmore-faq5Question', text: faqContent.faq5Question },
				{ id: 'learnmore-faq5Answer', text: faqContent.faq5Answer }
			];
			
			this.translationService.translatePage(
				this.employer.employerID,
				'/learn-more',
				items
			).subscribe(translations => {
				this.faq1Question = translations['learnmore-faq1Question'] || faqContent.faq1Question;
				this.faq1Answer1 = translations['learnmore-faq1Answer1'] || faqContent.faq1Answer1;
				this.faq1Answer2 = translations['learnmore-faq1Answer2'] || faqContent.faq1Answer2;
				this.faq1Answer3 = translations['learnmore-faq1Answer3'] || faqContent.faq1Answer3;
				this.faq2Question = translations['learnmore-faq2Question'] || faqContent.faq2Question;
				this.faq2Answer = translations['learnmore-faq2Answer'] || faqContent.faq2Answer;
				this.faq3Question = translations['learnmore-faq3Question'] || faqContent.faq3Question;
				this.faq3Answer = translations['learnmore-faq3Answer'] || faqContent.faq3Answer;
				this.faq4Question = translations['learnmore-faq4Question'] || faqContent.faq4Question;
				this.faq4Answer = translations['learnmore-faq4Answer'] || faqContent.faq4Answer;
				this.faq4SubAnswer1 = translations['learnmore-faq4SubAnswer1'] || faqContent.faq4SubAnswer1;
				this.faq4SubAnswer2 = translations['learnmore-faq4SubAnswer2'] || faqContent.faq4SubAnswer2;
				this.faq4SubAnswer3 = translations['learnmore-faq4SubAnswer3'] || faqContent.faq4SubAnswer3;
				this.faq5Question = translations['learnmore-faq5Question'] || faqContent.faq5Question;
				this.faq5Answer = translations['learnmore-faq5Answer'] || faqContent.faq5Answer;
			});
		}
	}

	constructor(
		dataService: DataService,
		private route: ActivatedRoute,
		siteService: SiteService,
		translationService: TranslationService,
		changeDetector: ChangeDetectorRef
	) {
		super(dataService, siteService, changeDetector, translationService);
		
		// Subscribe to language changes
		this.translationService.currentLanguage$.subscribe(lang => {
			this.currentLanguage = lang;
		});
		
		this.route.parent?.parent?.data.subscribe(data => {
			this.path = data['path'] || window.location.hostname.split('.')[0];
		});
	}

	ngOnInit(): void {
		this.siteService.onResize.subscribe(size => {
			switch(size) {
				case 's': this.topicCardsPerGroup = 2; break;
				case 'm': this.topicCardsPerGroup = 2; break;
				case 'l': this.topicCardsPerGroup = 3; break;
				case 'xl': this.topicCardsPerGroup = 4; break;
			}
			this.layoutTopicCards();
		});
		this.route.paramMap.pipe(
			tap(p => {
				this.path = p.get('path') || window.location.hostname.split('.')[0];
			}),
			switchMap(p => {
				if (!this.path) {
					throw new Error('Path is required');
				}
				return this.dataService.getEmployerIdFromPath(this.path);
			}),
		switchMap(id => this.dataService.getEmployer(id))
	).subscribe((e) => {
		this.loadEmployerData(e);
	});
}

	ngAfterViewInit() {
		const timer = setInterval(() => {
			if (document.querySelector('.fade-in')) {
				clearInterval(timer);
				this.siteService.pageInitDone();
			}
		}, 500);
	}
	loadMore(): void {
		let nextTopicCardsToShow = this.topicCards.slice(this.topicCardGroupsVisible * this.topicCardsPerGroup, ++this.topicCardGroupsVisible * this.topicCardsPerGroup);
		nextTopicCardsToShow.forEach((c,i) => {
			setTimeout(() => { c.added = true; }, i * 250);
			this.topicCardsToShow.push(c);
		});
	}
	showLess(): void {
		this.topicCardsToShow = this.topicCardsToShow.slice(0, this.topicCardsPerGroup);
		this.topicCards.forEach((c,i) => {
			if(i >= this.topicCardsPerGroup) {
				c.added = false;
			}
		})
		this.topicCardGroupsVisible = 1;
	}

	layoutTopicCards(): void {
		this.topicCardsToShow = this.topicCards?.slice(0, this.topicCardGroupsVisible * this.topicCardsPerGroup).map(c => new ContentCard({ ...c, added: true }));
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// Base class abstract method implementations
	
	protected getPagePath(): string {
		return '/learn-more';
	}

	protected getCards(): Array<ContentCard> {
		return this.topicCards;
	}

	protected getCardPrefix(): string {
		return 'topic-card';
	}

	protected loadCards(): void {
		this.topicCards = this.employer.employerCards.cardContainers.find(c => c.cardContainerID == 2)?.cards.map(c => new ContentCard(c));
		this.topicCardsToShow = this.topicCards?.slice(0, this.topicCardGroupsVisible * this.topicCardsPerGroup).map(c => new ContentCard({ ...c, added: true }));
	}

	protected updatePageContent(): void {
		this.updateHeroBackground();
		this.updateTextsWithHtml();
		this.updateFaqs();
	}

	protected onLanguageChange(): void {
		this.updateTextsWithHtml();
		this.updateFaqs();
	}

	protected applyCardSpecificTranslations(): void {
		// Refresh topicCardsToShow to reflect the translations
		this.topicCardsToShow = this.topicCards.slice(0, this.topicCardGroupsVisible * this.topicCardsPerGroup).map(c => new ContentCard({ ...c, added: true }));
	}

}
